import { useState } from "react";

import { proactivePromptStyleSchema, type Settings } from "@go-novus/admin-api";
import { Panel, SelectField } from "@go-novus/ui";

import type { ProactiveRuleDraft } from "#/features/settings";
import * as m from "#/paraglide/messages";

import type { ElementPickControl } from "../../hooks/use-element-pick";
import type { PreviewControl } from "../../hooks/use-preview-control";
import type { DraftErrors } from "../../lib/settings-draft";
import { PreviewLayout, type DraftView } from "../preview";
import { ProactiveRulesSection } from "./proactive-rules-section";

type Chatbox = Settings["chatbox"];

const PROMPT_STYLE_LABEL: Record<
  Chatbox["proactivePromptStyle"],
  () => string
> = {
  bubble: m.settings_prompt_style_bubble,
  body: m.settings_prompt_style_body,
};

export interface ProactiveSectionProps {
  draftView: DraftView;
  errors: DraftErrors;
  onChatboxChange: (next: Chatbox) => void;
  onRulesChange: (rules: ProactiveRuleDraft[]) => void;
  pick: ElementPickControl;
  preview: PreviewControl;
}

/**
 * Proactive messages 标签：最上面是全店统一的 Prompt style，下面是主动消息
 * 规则列表；右边摆各标签共用的那张预览，视图跟着标签对齐、也可手动切换。
 */
export function ProactiveSection({
  draftView,
  errors,
  onChatboxChange,
  onRulesChange,
  pick,
  preview,
}: ProactiveSectionProps) {
  const { chatbox, proactiveRules } = draftView;
  // 「最后操作过的规则」只活在这个标签页里：离开标签页组件卸载，回到「没操作过」。
  const [operatedRuleId, setOperatedRuleId] = useState<string>();

  return (
    <PreviewLayout
      draftView={draftView}
      operatedRuleId={operatedRuleId}
      preview={preview}
    >
      <Panel>
        <div className="p-4.5">
          <SelectField
            label={m.settings_prompt_style_label()}
            value={chatbox.proactivePromptStyle}
            options={proactivePromptStyleSchema.options.map((value) => ({
              label: PROMPT_STYLE_LABEL[value](),
              value,
            }))}
            onValueChange={(value) => {
              const style = proactivePromptStyleSchema.safeParse(value);
              if (style.success) {
                onChatboxChange({
                  ...chatbox,
                  proactivePromptStyle: style.data,
                });
              }
            }}
          />
          <p className="mt-1.75 text-micro [line-height:1.45] text-[var(--content-fg-subtle)]">
            {m.settings_prompt_style_hint()}
          </p>
        </div>
      </Panel>
      <ProactiveRulesSection
        errors={errors}
        onChange={onRulesChange}
        onOperateRule={setOperatedRuleId}
        pick={pick}
        rules={proactiveRules}
      />
    </PreviewLayout>
  );
}
